import { useState } from 'react';
import { IconStar, IconStarFilled, IconMessage2, IconX } from '@tabler/icons-react';
import Cookies from 'js-cookie';
import BACKEND_URL from '../config';
import Modal from './ui/Modal';
import type { DocFeedbackSummary } from './Documents';

interface DocumentationRetrievedFeedbackProps {
    documentation_retrieved_id: number;
    feedback?: DocFeedbackSummary;
    onFeedbackSubmitted?: (docId: number, rating: number) => void;
}

const stars = [1, 2, 3, 4, 5] as const;

const DocumentationRetrievedFeedback = ({ documentation_retrieved_id, feedback, onFeedbackSubmitted }: DocumentationRetrievedFeedbackProps) => {
    const [rating, setRating] = useState<number>(0);
    const [hoverRating, setHoverRating] = useState<number>(0);
    const [comment, setComment] = useState<string>('');
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [showModal, setShowModal] = useState<boolean>(false);

    const effectiveRating = hoverRating || rating;
    const feedbackCount = feedback?.count ?? 0;
    const displayRating = feedback?.latestRating ?? null;

    const onSubmit = async () => {
        setSubmitting(true);
        setError('');
        try {
            const response = await fetch(`${BACKEND_URL}/vnog/create_documentation_retrieved_feedback/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': Cookies.get('csrftoken') || '',
                },
                credentials: 'include',
                body: JSON.stringify({
                    documentation_retrieved_id,
                    comment,
                    rating,
                }),
            });
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error((data && data.error) || 'Failed to submit feedback');
            }
            onFeedbackSubmitted?.(documentation_retrieved_id, rating);
            setShowModal(false);
            setRating(0);
            setComment('');
        } catch (e: any) {
            setError(e?.message || 'Something went wrong');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="w-full">
            <div className="inline-flex items-center gap-2">
                {feedbackCount > 0 && displayRating !== null && (
                    <div className="inline-flex items-center gap-1" aria-label={`Feedback: ${displayRating}/5`}>
                        {stars.map((star) => (
                            star <= displayRating ? (
                                <IconStarFilled key={star} size={18} className="text-amber-400" />
                            ) : (
                                <IconStar key={star} size={18} className="text-amber-300" />
                            )
                        ))}
                        <span className="ml-1 text-sm text-gray-600">({feedbackCount})</span>
                    </div>
                )}
                <button
                    type="button"
                    onClick={() => setShowModal(true)}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-gray-900 text-white text-sm hover:bg-black transition-colors"
                >
                    <IconMessage2 size={18} />
                    Feedback
                </button>
            </div>

            <Modal isOpen={showModal} onRequestClose={() => setShowModal(false)}>
                <div className="w-full max-w-lg mx-auto">
                    <div className="relative rounded-xl bg-white shadow-lg border border-gray-100 p-5">
                        <button
                            type="button"
                            onClick={() => setShowModal(false)}
                            className="absolute top-3 right-3 p-1 rounded hover:bg-gray-100 text-gray-500"
                            aria-label="Close"
                        >
                            <IconX size={18} />
                        </button>

                        <div className="w-full">
                            <div className="mb-3">
                                <h3 className="text-base font-semibold text-gray-900">Rate these results</h3>
                                <p className="text-sm text-gray-500 mt-1">
                                    How helpful was this retrieved documentation for your current situation? Select a rating and add an optional comment.
                                </p>
                            </div>

                            <div className="flex items-center gap-1 mb-3" aria-label="Star rating selector">
                                {stars.map((star) => {
                                    const active = star <= effectiveRating;
                                    return (
                                        <button
                                            key={star}
                                            type="button"
                                            className="p-1 transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-300 rounded"
                                            onMouseEnter={() => setHoverRating(star)}
                                            onMouseLeave={() => setHoverRating(0)}
                                            onClick={() => setRating(star)}
                                            aria-label={`${star} star${star > 1 ? 's' : ''}`}
                                        >
                                            {active ? (
                                                <IconStarFilled size={28} className="text-amber-400" />
                                            ) : (
                                                <IconStar size={28} className="text-amber-300" />
                                            )}
                                        </button>
                                    );
                                })}
                                <span className="ml-2 text-sm text-gray-600">{rating > 0 ? `${rating}/5` : 'Select a rating'}</span>
                            </div>

                            <div className="mb-3">
                                <label htmlFor="feedback-comment" className="block text-sm font-medium text-gray-700 mb-1">
                                    Add a brief comment (optional)
                                </label>
                                <textarea
                                    id="feedback-comment"
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    rows={3}
                                    placeholder="What worked well? What could be improved?"
                                    className="w-full resize-y rounded-lg border border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none p-2 text-sm text-gray-800 placeholder-gray-400"
                                />
                                <p className="text-xs text-gray-400 mt-1">Your feedback may be reviewed to improve retrieval quality.</p>
                            </div>

                            {error && (
                                <div className="mb-3 text-sm text-red-600">{error}</div>
                            )}

                            <div className="flex items-center justify-end gap-2">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-3 py-2 rounded-md text-sm font-medium border border-gray-200 text-gray-700 hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    disabled={submitting || rating === 0}
                                    onClick={onSubmit}
                                    className={`px-4 py-2 rounded-md text-white text-sm font-medium transition-colors ${
                                        submitting || rating === 0
                                            ? 'bg-gray-300 cursor-not-allowed'
                                            : 'bg-blue-600 hover:bg-blue-700'
                                    }`}
                                >
                                    {submitting ? 'Submitting…' : 'Submit feedback'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    )
}

export default DocumentationRetrievedFeedback;
